import React, { useCallback, useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import { usersAPI, rolesAPI } from '../../api';
import { useAuth } from '../../contexts/AuthContext';
import { useAppStore } from '../../stores/useAppStore';
import { Button } from '../ui/Button';
import { Modal } from '../ui/Modal';
import UsersTabs from './UsersTabs';
import UserFormModal from './UserFormModal';
import PasswordModal from './PasswordModal';
import { apiError, sortPeople } from './usersUtils';

const linkButton = 'text-sm font-medium text-blue-600 hover:text-blue-800 disabled:text-gray-400';

const RolePill = ({ role }) => (
  <span
    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-sm font-medium ${
      role.code === 'admin' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-700'
    }`}
  >
    {role.name}
  </span>
);

const StatusPill = ({ active }) =>
  active ? (
    <span className="inline-flex items-center gap-1.5 text-sm text-green-700">
      <span className="h-2 w-2 rounded-full bg-green-500" aria-hidden="true" />
      On
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 text-sm text-gray-500">
      <span className="h-2 w-2 rounded-full bg-gray-400" aria-hidden="true" />
      Off
    </span>
  );

/** Users page: everyone who can sign in, with their role, limit and on/off. */
const UsersPage = () => {
  const { user: me } = useAuth();
  const { addNotification } = useAppStore();
  const [people, setPeople] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [editing, setEditing] = useState(undefined); // undefined = closed, null = new person
  const [passwordFor, setPasswordFor] = useState(null);
  const [switchingOff, setSwitchingOff] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const [usersRes, rolesRes] = await Promise.all([usersAPI.list(), rolesAPI.list()]);
      setPeople(sortPeople(usersRes.data));
      setRoles(rolesRes.data.roles);
    } catch (error) {
      setLoadError(apiError(error, 'Could not load the people. Please try again.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const putPerson = (saved) => {
    setPeople((list) => sortPeople([...list.filter((p) => p.id !== saved.id), saved]));
  };

  const handleSaved = (saved) => {
    const isNew = editing === null;
    setEditing(undefined);
    putPerson(saved);
    addNotification({
      type: 'success',
      message: isNew ? `${saved.full_name || saved.username} can now sign in.` : 'Saved.',
    });
  };

  const setActive = async (person, active) => {
    setBusyId(person.id);
    try {
      const response = await usersAPI.update(person.id, { is_active: active });
      putPerson(response.data);
      addNotification({
        type: 'success',
        message: active
          ? `${person.full_name || person.username} is switched on.`
          : `${person.full_name || person.username} is switched off.`,
      });
    } catch (error) {
      addNotification({ type: 'error', message: apiError(error, 'Could not save. Please try again.') });
    } finally {
      setBusyId(null);
      setSwitchingOff(null);
    }
  };

  const offCount = people.filter((p) => !p.is_active).length;

  return (
    <div>
      <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Users</h1>
          <p className="text-sm text-gray-500">People who can sign in to this shop&apos;s POS, and what they can do.</p>
        </div>
        <Button onClick={() => setEditing(null)} disabled={loading || !!loadError}>
          <Plus className="h-4 w-4 mr-1" />
          Add person
        </Button>
      </div>

      <UsersTabs />

      {loading ? (
        <div className="flex justify-center items-center p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : loadError ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {loadError}{' '}
          <button onClick={load} className="font-medium underline">
            Try again
          </button>
        </div>
      ) : (
        <>
          <p className="mb-2 text-sm text-gray-500">
            {people.length} {people.length === 1 ? 'person' : 'people'}
            {offCount > 0 && ` · ${offCount} switched off`}
          </p>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    {['Name', 'Role', 'Discount limit', 'Status', ''].map((title, i) => (
                      <th key={i} className="px-4 py-3 text-left text-sm font-semibold text-gray-600">
                        {title}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {people.map((person) => {
                    const isMe = person.id === me?.id;
                    return (
                      <tr key={person.id} className={person.is_active ? 'hover:bg-gray-50' : 'bg-gray-50/60 text-gray-500'}>
                        <td className="px-4 py-3">
                          <div className={`font-medium ${person.is_active ? 'text-gray-900' : 'text-gray-500'}`}>
                            {person.full_name || person.username}
                            {isMe && <span className="ml-2 text-sm font-normal text-gray-500">(you)</span>}
                          </div>
                          <div className="text-sm text-gray-500">{person.username}</div>
                        </td>
                        <td className="px-4 py-3">
                          <RolePill role={person.role} />
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {person.effective_discount_limit}%
                          {person.discount_limit !== null && person.discount_limit !== undefined && (
                            <span className="ml-1 text-gray-500">(own)</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <StatusPill active={person.is_active} />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap justify-end gap-x-4 gap-y-1">
                            <button className={linkButton} onClick={() => setEditing(person)}>
                              Edit
                            </button>
                            <button className={linkButton} onClick={() => setPasswordFor(person)}>
                              New password
                            </button>
                            {person.is_active ? (
                              !isMe && (
                                <button
                                  className="text-sm font-medium text-red-600 hover:text-red-800 disabled:text-gray-400"
                                  onClick={() => setSwitchingOff(person)}
                                  disabled={busyId === person.id}
                                >
                                  Switch off
                                </button>
                              )
                            ) : (
                              <button
                                className={linkButton}
                                onClick={() => setActive(person, true)}
                                disabled={busyId === person.id}
                              >
                                Switch on
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          <p className="mt-3 text-sm text-gray-500">
            People are never deleted, so old sales still show who made them. Switch them off instead.
          </p>
        </>
      )}

      <UserFormModal
        isOpen={editing !== undefined}
        onClose={() => setEditing(undefined)}
        person={editing || null}
        roles={roles}
        isSelf={Boolean(editing && editing.id === me?.id)}
        onSaved={handleSaved}
      />

      <PasswordModal
        isOpen={!!passwordFor}
        onClose={() => setPasswordFor(null)}
        person={passwordFor}
        onSaved={(person) => {
          setPasswordFor(null);
          addNotification({
            type: 'success',
            message: `New password saved for ${person.full_name || person.username}.`,
          });
        }}
      />

      <Modal
        isOpen={!!switchingOff}
        onClose={() => setSwitchingOff(null)}
        title={switchingOff ? `Switch off ${switchingOff.full_name || switchingOff.username}?` : ''}
        size="sm"
      >
        {switchingOff && (
          <div className="space-y-4">
            <p className="text-sm text-gray-700">They can&apos;t sign in until you switch them on again.</p>
            <p className="text-sm text-gray-700">If they are signed in now, they are signed out at their next click.</p>
            <div className="flex justify-end gap-3">
              <Button variant="secondary" onClick={() => setSwitchingOff(null)} autoFocus>
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={() => setActive(switchingOff, false)}
                disabled={busyId === switchingOff.id}
              >
                Switch off
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default UsersPage;
